import { type LucideIcon, ShieldAlert, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { ErrorCodes, errorCode, errorSentence } from '../../components/ErrorToast';
import { Button, Card, EmptyState } from '../../components/ui';
import { ApiError } from '../../lib/errors';
import { SITE } from './texts';

/* Общие части «Настройки → Площадки» (007): код ошибки, ошибки загрузки, копирование текста. */

export const SCREEN = 'Settings/Sites';

/** Сбой связи, сервера или ответ не по договору — в серверный журнал (принцип II); отказ по правам и данным — нет. */
export const shouldReport = (e: unknown) =>
  !(e instanceof ApiError) || e.code === 'NETWORK' || e.code === 'INTERNAL' || e.status >= 500;

export const isForbidden = (e: unknown): e is ApiError => e instanceof ApiError && e.status === 403;
export const isNotFound = (e: unknown): e is ApiError => e instanceof ApiError && e.status === 404;

/** Код ошибки (requestId) для команды разработки; если текст ошибки — слова сервера, рядом — код ответа. */
export function ErrorCode({ error }: { error: unknown }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  if (!requestId && !code) return null;
  return (
    <p className="text-[12px] text-ink-3">
      {SITE.errorCode} <ErrorCodes requestId={requestId} code={code} className="text-ink-2" />
    </p>
  );
}

/** Не загрузилось: причина, код ошибки и «Повторить». */
export function LoadError({
  title,
  error,
  busy,
  onRetry,
}: { title: string; error: unknown; busy: boolean; onRetry: () => void }) {
  return (
    <Card>
      <EmptyState
        icon={TriangleAlert}
        title={title}
        description={errorSentence(error)}
        action={
          <div className="flex flex-col items-center gap-3">
            <ErrorCode error={error} />
            <Button variant="primary" disabled={busy} onClick={onRetry}>
              {SITE.retry}
            </Button>
          </div>
        }
      />
    </Card>
  );
}

/**
 * Поле формы (дизайн-система Field): подпись над полем, подсказка `caption` под ним; ошибка встаёт на место подсказки
 * (`color-bad`). Поле получает `id`, `aria-invalid` и `aria-describedby` через функцию-ребёнка.
 */
export function FormField({
  id,
  label,
  hint,
  error,
  className = '',
  children,
}: {
  id: string;
  label: string;
  hint?: ReactNode;
  error?: string | null;
  className?: string;
  children: (a: { id: string; 'aria-invalid'?: true; 'aria-describedby'?: string }) => ReactNode;
}) {
  const noteId = `${id}-note`;
  const note = error || hint;
  return (
    <div className={`flex min-w-0 flex-col gap-1.5 ${className}`}>
      <label htmlFor={id} className="text-[13px] font-medium text-ink">
        {label}
      </label>
      {children({
        id,
        ...(error ? { 'aria-invalid': true as const } : {}),
        ...(note ? { 'aria-describedby': noteId } : {}),
      })}
      {note && (
        <span id={noteId} className={`text-[12px] ${error ? 'text-bad' : 'text-ink-3'}`}>
          {note}
        </span>
      )}
    </div>
  );
}

/** Отказ, который повтор не исправит (403, 404): причина и код ошибки, без «Повторить». */
export function Refused({
  title,
  text,
  error,
  icon = ShieldAlert,
  action,
}: { title: string; text: string; error: unknown; icon?: LucideIcon; action?: ReactNode }) {
  return (
    <Card>
      <EmptyState
        icon={icon}
        title={title}
        description={text}
        action={
          <div className="flex flex-col items-center gap-3">
            <ErrorCode error={error} />
            {action}
          </div>
        }
      />
    </Card>
  );
}

/**
 * Копирование в буфер обмена: `navigator.clipboard`, при отказе — скрытое поле и `execCommand('copy')` (как в
 * прототипе). Вызывать в обработчике нажатия: Safari пускает в буфер обмена только так. Получилось — `true`.
 */
export async function copyText(value: string): Promise<boolean> {
  return (await viaClipboard(value)) || viaHiddenField(value);
}

/** `navigator.clipboard`; нет его или он отказал (страница без https, нет разрешения) — `false`. */
async function viaClipboard(value: string): Promise<boolean> {
  try {
    const done = navigator.clipboard?.writeText(value);
    if (!done) return false;
    await done;
    return true;
  } catch {
    return false;
  }
}

/** Запасной путь: скрытое поле и `execCommand('copy')`; фокус возвращается на кнопку. */
function viaHiddenField(value: string): boolean {
  if (typeof document.execCommand !== 'function') return false;
  const back = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const ta = document.createElement('textarea');
  ta.value = value;
  ta.setAttribute('readonly', '');
  ta.setAttribute('aria-hidden', 'true');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    ta.remove();
    back?.focus();
  }
}
